import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axiosInstance from 'Services/APIService';

export interface FormData {
  id?: string;
  name: string;
  username?: string;
  department?: string;
  role?: { role_id: string; label: string }[];
  gender?: string;
  phone: string;
  email: string;
  isActive?: boolean;
  dob: Date;
  address?: string;
  projects?: string[];
  groups?: string[];
  avartar?: string;
  creation_date_time?: string;
  created_by?: string;
  status: 'active' | 'inactive' | 'verify pending' | 'approval pending' | 'rejected';
}

export interface MembersReviewState {
  members: FormData[];
  selectedMember: FormData | null;
  selectedIds: string[];
  loading: boolean;
  error: string | null;
  avatarLoading: boolean;
  avatarUrls: { [key: string]: string };
  avatarError: string | null;
  avatarFetching: { [key: string]: boolean };
  attemptedAvatarFetches: { [key: string]: boolean };
  activeTab: 'Verification Pending' | 'Approval Pending' | 'Rejected Applicants' | 'Approved';
}

interface AsyncThunkConfig {
  state: { membersReview: MembersReviewState };
  rejectValue: string;
}

interface JsonData {
  id?: string;
  name: string;
  username?: string;
  department?: string;
  role?: string[];
  gender?: string;
  phone: string;
  email: string;
  dob: string;
  address?: string;
  projects?: string[];
  groups?: string[];
  avatar?: string;
  creation_date_time?: string;
  created_by?: string;
  approved_by?: string;
  suburb?: string;
  state?: string;
  country_res?: string;
  postcode?: string;
  group_id?: string;
  service_name_id?: string;
  component_parent_id?: string;
  nested_component_id?: string;
  user_whoRequested_id?: string;
  isActive?: boolean;
  status?: string;
}

const createGenericKeys = (
  data: FormData,
  selectedProjectId: string,
  selectedServiceTitle: string,
  componentParentId: string,
  user_whoRequested_id: string
) => ({
  dob: new Date(data.dob).toISOString(),
  group_id: selectedProjectId,
  service_name_id: selectedServiceTitle || '',
  component_parent_id: componentParentId,
  nested_component_id: 'Platform Admin Page',
  user_whoRequested_id: user_whoRequested_id || ''
});

const initialState: MembersReviewState = {
  members: [],
  selectedMember: null,
  selectedIds: [],
  loading: false,
  error: null,
  avatarLoading: false,
  avatarUrls: {},
  avatarError: null,
  avatarFetching: {},
  attemptedAvatarFetches: {},
  activeTab: 'Verification Pending'
};

// Retry helper
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const getAvatarUrl = async (avatarPath: string, retryCount = 1): Promise<string> => {
  if (!avatarPath) {
    console.log('No avatar path provided, using default');
    return '/default-avatar.png';
  }

  const attemptFetch = async (attempt: number): Promise<string> => {
    try {
      console.log(`Attempt ${attempt} to fetch avatar: ${avatarPath}`);
      const response = await axiosInstance.get('/avatar/get-avatar', {
        params: { path: avatarPath },
        responseType: 'blob'
      });

      const blobUrl = URL.createObjectURL(response.data);
      console.log(`Avatar fetched successfully: ${blobUrl}`);
      return blobUrl;
    } catch (error: any) {
      console.error(`Error fetching avatar (attempt ${attempt}) for path ${avatarPath}:`, {
        status: error.response?.status,
        message: error.message,
        data: error.response?.data
      });
      if (attempt < retryCount) {
        console.log(`Retrying avatar fetch for ${avatarPath} after 1s...`);
        await delay(1000);
        return attemptFetch(attempt + 1);
      }
      return '/default-avatar.png';
    }
  };

  return attemptFetch(1);
};

// Helper to map API response to FormData
const mapApiResponseToFormData = (applicant: any, tab: string): FormData => {
  console.log('applicant', applicant);
  const roles = JSON.parse(localStorage.getItem('roles') || '[]') as {
    role_id: string;
    label: string;
  }[];
  let roleData: { role_id: string; label: string }[] = [];

  if (applicant.role) {
    const roleId = Array.isArray(applicant.role) ? applicant.role[0] : applicant.role;
    const role = roles.find((r) => r.role_id === roleId);
    if (role) {
      roleData = [role];
    } else {
      roleData = [{ role_id: roleId, label: roleId }];
    }
  }

  if (tab === 'Approved') {
    const userDetails = applicant.user_details || {};
    console.log('applicant', applicant);
    return {
      id: applicant.id || '',
      name: userDetails.name || applicant.username || '',
      email: userDetails.email || applicant.username || '',
      phone: applicant.phone || '',
      address:
        applicant.address ||
        `${applicant.suburb || ''}, ${applicant.state || ''}, ${applicant.country_res || ''}, ${
          applicant.postcode || ''
        }`
          .trim()
          .replace(/,\s*,+/g, ',') // clean multiple commas
          .replace(/^,|,$/g, '') ||
        '',

      gender: applicant.gender || '',
      dob: applicant.dob ? new Date(applicant.dob) : new Date(),
      role: userDetails.role
        ? [
            {
              role_id: userDetails.role,
              label: roles.find((r) => r.role_id === userDetails.role)?.label || userDetails.role
            }
          ]
        : [],
      department: applicant.department || '',
      creation_date_time: applicant.approved_on || undefined,
      // created_by: applicant.approved_by || '',
      created_by: Array.isArray(applicant.approved_by)
        ? applicant.approved_by.join(', ')
        : applicant.approved_by || '',

      groups: applicant.groups?.map((g: { group_id: string }) => g.group_id) || [],
      projects: applicant.projects || [],
      avartar: applicant.avatar || '',
      username: applicant.username || '',
      status: 'active',
      isActive: applicant.isActive ?? true
    };
  }

  return {
    id: applicant.id || '',
    name: applicant.name || '',
    email: applicant.email || '',
    phone: applicant.phone || '',
    address:
      applicant.address ||
      `${applicant.suburb || ''}, ${applicant.state || ''}, ${applicant.country_res || ''}, ${
        applicant.postcode || ''
      }`
        .trim()
        .replace(/,\s*,+/g, ',') // clean multiple commas
        .replace(/^,|,$/g, '') ||
      '',
    gender: applicant.gender || '',
    dob: applicant.dob ? new Date(applicant.dob) : new Date(),
    role: roleData,
    department: applicant.department || '',
    creation_date_time: applicant.creation_date_time || undefined,
    created_by: applicant.created_by || '',
    groups: applicant.groups?.map((g: { group_id: string }) => g.group_id) || [],
    projects: [],
    avartar: applicant.avatar || '',
    username: applicant.username || '',
    status:
      applicant.status === 'VerificationPending'
        ? 'verify pending'
        : applicant.status === 'ApprovalPending'
        ? 'approval pending'
        : applicant.status === 'Rejected'
        ? 'rejected'
        : 'inactive',
    isActive: applicant.isActive ?? true
  };
};

// Map tab names to API status values
const tabToStatusMap: Record<
  'Verification Pending' | 'Approval Pending' | 'Rejected Applicants' | 'Approved',
  string
> = {
  'Verification Pending': 'Verification Pending',
  'Approval Pending': 'Approval Pending',
  'Rejected Applicants': 'Rejected Applicants',
  Approved: 'Approved Members'
};

// Fetch applicants
export const fetchApplicants = createAsyncThunk<
  {
    members: FormData[];
    flag: 'Verification Pending' | 'Approval Pending' | 'Rejected Applicants' | 'Approved';
  },
  'Verification Pending' | 'Approval Pending' | 'Rejected Applicants' | 'Approved',
  AsyncThunkConfig
>(
  'membersReview/fetchApplicants',
  async (
    flag: 'Verification Pending' | 'Approval Pending' | 'Rejected Applicants' | 'Approved',
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get(
        `/newapplicants/list?flag=${encodeURIComponent(tabToStatusMap[flag])}&t=${Date.now()}`
      );
      console.log(`Fetch applicants response for ${flag}:`, response.data);
      const members = response.data.map((applicant: any) =>
        mapApiResponseToFormData(applicant, flag)
      );
      return { members, flag };
    } catch (error) {
      console.error('Error fetching applicants:', error);
      return rejectWithValue(`Failed to fetch applicants for ${flag}`);
    }
  }
);

// Add new member
export const addMember = createAsyncThunk<
  FormData,
  {
    data: FormData;
    selectedProjectId: string;
    selectedServiceTitle: string;
    loggedInWorkerName: string;
    componentParentId: string;
    user_whoRequested_id: string;
    avatarFile: File | null;
  },
  AsyncThunkConfig
>(
  'membersReview/addMember',
  async (
    {
      data,
      selectedProjectId,
      selectedServiceTitle,
      loggedInWorkerName,
      componentParentId,
      user_whoRequested_id,
      avatarFile
    },
    { rejectWithValue }
  ) => {
    try {
      const jsonData: JsonData = {
        ...data,
        ...createGenericKeys(
          data,
          selectedProjectId,
          selectedServiceTitle,
          componentParentId,
          user_whoRequested_id
        ),
        created_by: loggedInWorkerName,
        avatar: data.avartar || '',
        role: (() => {
          const roles = data.role?.map((r) => r.role_id) || [];
          return roles.includes('bronze-community-10pf') ? roles : [...roles, 'bronze-community-10pf'];
        })(),
        groups: data.groups || [],
        status: data.status
      };

      const addressParts = (data.address || '').split(',').map((part) => part.trim());
      const [suburb, state, country_res, postcode] =
        addressParts.length >= 4
          ? addressParts
          : [
              addressParts[0] || '',
              addressParts[1] || '',
              addressParts[2] || '',
              addressParts[3] || ''
            ];

      jsonData.suburb = suburb;
      jsonData.state = state;
      jsonData.country_res = country_res;
      jsonData.postcode = postcode;

      const formData = new FormData();
      formData.append('json', JSON.stringify(jsonData));
      if (avatarFile) {
        formData.append('image', avatarFile);
      }

      const response = await axiosInstance.post('/members/add', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Add member response:', response.data);
      if (!response.data.membership_applicants || !response.data.membership_applicants[0]) {
        throw new Error('Invalid response format');
      }
      return mapApiResponseToFormData(response.data.membership_applicants[0], 'Approved');
    } catch (error) {
      console.error('Error adding member:', error);
      return rejectWithValue('Failed to add member');
    }
  }
);

// Update member
export const updateMember = createAsyncThunk<
  FormData,
  {
    data: FormData;
    selectedProjectId: string;
    selectedServiceTitle: string;
    loggedInWorkerName: string;
    componentParentId: string;
    user_whoRequested_id: string;
    avatarFile: File | null;
  },
  AsyncThunkConfig
>(
  'membersReview/updateMember',
  async (
    {
      data,
      selectedProjectId,
      selectedServiceTitle,
      loggedInWorkerName,
      componentParentId,
      user_whoRequested_id,
      avatarFile
    },
    { rejectWithValue }
  ) => {
    try {
      const jsonData: JsonData = {
        id: data.id || '',
        name: data.name || '',
        gender: data.gender || '',
        email: data.email || '',
        phone: data.phone || '',
        dob: new Date(data.dob).toISOString().split('T')[0],
        department: data.department || '',
        role: (() => {
          const roles = data.role?.map((r) => r.role_id) || [];
          return roles.includes('bronze-community-10pf') ? roles : [...roles, 'bronze-community-10pf'];
        })(),
        groups: data.groups || [],
        avatar: data.avartar || '',
        approved_by: loggedInWorkerName || 'admin',
        isActive: data.isActive ?? true
      };

      const addressParts = (data.address || '').split(',').map((part) => part.trim());
      const [suburb, state, country_res, postcode] =
        addressParts.length >= 4
          ? addressParts
          : [
              addressParts[0] || '',
              addressParts[1] || '',
              addressParts[2] || '',
              addressParts[3] || ''
            ];

      jsonData.address = data.address;
      jsonData.suburb = suburb;
      jsonData.state = state;
      jsonData.country_res = country_res;
      jsonData.postcode = postcode;

      const formData = new FormData();
      formData.append('json', JSON.stringify(jsonData));
      if (avatarFile) {
        formData.append('image', avatarFile);
      }

      console.log('Sending update member request with FormData:', {
        json: jsonData,
        image: avatarFile?.name
      });

      const response = await axiosInstance.post('/membership/update-member', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Update member API response:', response);
      if (response.status !== 200) {
        throw new Error('Failed to update member: Non-200 status');
      }

      return { ...data, avartar: data.avartar || '' };
    } catch (error) {
      console.error('Error updating member:', error);
      return rejectWithValue('Failed to update member');
    }
  }
);

// Perform applicant action
export const performApplicantAction = createAsyncThunk<
  { memberIds: string[]; action: 'approve' | 'reject' | 'delete' },
  {
    membersData: { id: string; email: string }[];
    action: 'approve' | 'reject' | 'delete';
    tab: 'Verification Pending' | 'Approval Pending' | 'Rejected Applicants' | 'Approved';
  },
  AsyncThunkConfig
>(
  'membersReview/performApplicantAction',
  async (
    {
      membersData,
      action,
      tab
    }: {
      membersData: { id: string; email: string }[];
      action: 'approve' | 'reject' | 'delete';
      tab: 'Verification Pending' | 'Approval Pending' | 'Rejected Applicants' | 'Approved';
    },
    { rejectWithValue }
  ) => {
    try {
      const payload = membersData.map((member) => ({
        id: member.id,
        email: member.email,
        action,
        status: tabToStatusMap[tab]
      }));
      const response = await axiosInstance.post('/applicant/action', payload);
      console.log('Perform applicant action response:', response.data);
      return { memberIds: membersData.map((m) => m.id), action };
    } catch (error) {
      console.error('Error performing applicant action:', error);
      return rejectWithValue(`Failed to perform ${action} action`);
    }
  }
);

const membersReviewSlice = createSlice({
  name: 'membersReview',
  initialState,
  reducers: {
    setSelectedMember: (state, action: PayloadAction<FormData | null>) => {
      state.selectedMember = action.payload;
    },
    setSelectedIds: (state, action: PayloadAction<string[]>) => {
      state.selectedIds = action.payload;
    },
    clearAvatarError: (state) => {
      state.avatarError = null;
    },
    updateAvatarUrl: (state, action: PayloadAction<{ id: string; url: string }>) => {
      state.avatarUrls[action.payload.id] = action.payload.url;
    },
    setActiveTab: (
      state,
      action: PayloadAction<
        'Verification Pending' | 'Approval Pending' | 'Rejected Applicants' | 'Approved'
      >
    ) => {
      state.activeTab = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchApplicants.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchApplicants.fulfilled,
        (
          state,
          action: PayloadAction<{
            members: FormData[];
            flag: 'Verification Pending' | 'Approval Pending' | 'Rejected Applicants' | 'Approved';
          }>
        ) => {
          state.loading = false;
          state.members = action.payload.members;
          state.activeTab = action.payload.flag;
          Object.keys(state.avatarUrls).forEach((id) => {
            if (
              !action.payload.members.some((m) => m.id === id) &&
              state.avatarUrls[id].startsWith('blob:')
            ) {
              URL.revokeObjectURL(state.avatarUrls[id]);
              delete state.avatarUrls[id];
            }
          });
          state.avatarFetching = {};
          state.attemptedAvatarFetches = {};
        }
      )
      .addCase(fetchApplicants.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(addMember.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addMember.fulfilled, (state, action: PayloadAction<FormData>) => {
        state.loading = false;
        if (state.activeTab === 'Approved') {
          state.members.push(action.payload);
        }
      })
      .addCase(addMember.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateMember.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.avatarLoading = true;
      })
      .addCase(updateMember.fulfilled, (state, action: PayloadAction<FormData>) => {
        state.loading = false;
        state.avatarLoading = false;
        if (state.activeTab === 'Approved') {
          const index = state.members.findIndex((m) => m.id === action.payload.id);
          if (index !== -1) {
            state.members[index] = action.payload;
          }
          if (state.selectedMember?.id === action.payload.id) {
            state.selectedMember = action.payload;
          }
        }
      })
      .addCase(updateMember.rejected, (state, action) => {
        state.loading = false;
        state.avatarLoading = false;
        state.error = action.payload as string;
      })
      .addCase(performApplicantAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        performApplicantAction.fulfilled,
        (state, reduxAction: PayloadAction<{ memberIds: string[]; action: string }>) => {
          state.loading = false;
          const { memberIds, action } = reduxAction.payload;
          if (action === 'approve') {
            state.members = state.members.map((m) =>
              memberIds.includes(m.id!) ? { ...m, status: 'active' } : m
            );
          } else if (action === 'reject') {
            state.members = state.members.map((m) =>
              memberIds.includes(m.id!) ? { ...m, status: 'rejected' } : m
            );
          } else if (action === 'delete') {
            state.members = state.members.filter((m) => !memberIds.includes(m.id!));
          }
          state.selectedIds = state.selectedIds.filter((id) => !memberIds.includes(id));
        }
      )
      .addCase(performApplicantAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const {
  setSelectedMember,
  setSelectedIds,
  clearAvatarError,
  updateAvatarUrl,
  setActiveTab
} = membersReviewSlice.actions;

export default membersReviewSlice.reducer;
